import { createReducer, on } from '@ngrx/store';
import { RouterActions, AppActions, AuthActions } from '@actions/index'
import { NotificationModel } from '@models/notification.model';
import {excludeAppLoadingURLs} from '../../interceptors/header.interceptor';

/**
 * App State. Defines the app 
 * State that will store general
 * app information.
 */
export interface State {
    /**
     * error: a message of the current app error
     */
    error: string,
    /**
     * isExecutingTask: a flag that specifies 
     * whether a task is currently running or not
     */
    isExecutingTask: boolean,
    /**
     * taskCount: amount of tasks currently running
     */
    taskCount: number,
    /**
     * lastNotification: a {@link  NotificationModel} 
     * with the last app notification 
     */
    lastNotification: NotificationModel,
    /**
     * isShowIntercom: a boolean that indicates if 
     * intercom widget should be shown or not
     */
    isShowIntercom: boolean,
    /**
     * openSideDrawer: a boolean that indicates if 
     * the side menu is being displayed
     */
    openSideDrawer: boolean,
    /**
     * lastTimeActive: the time that has passed since
     * the last user activity was detected
     */
    lastTimeActive: number,
    /**
     * errorTimeInactive: a flag that indicates 
     * if the inactivity time has been met or not
     */
    errorTimeInactive: boolean,
    /**
     * inactivityTime: a time that indicates how 
     * long a user can be inactive
     */
    inactivityTime: number,
    redirectedFromGoogle: boolean,
    serverHour: string,
    errorTrialOver: boolean
}

/**
 * InitialState stores the initial values for general app state
 */
const initialState: State = {
    error: null,
    isExecutingTask: false,
    taskCount: 0,
    lastNotification: {
        id: '',
        type: '',
        dateCreated: 0
    },
    isShowIntercom: false,
    openSideDrawer: false,
    lastTimeActive: 0,
    errorTimeInactive: false,
    inactivityTime: 1200000,
    redirectedFromGoogle: false,
    serverHour: '',
    errorTrialOver: false
}

/**
 * reducer creates the app's reducer.
 * This reducer listens to all succesfull
 * app actions to store the returned 
 * data to app's app state depending
 * on the action
 */
export const reducer = createReducer(
    initialState,
    on(AppActions.executeTask, (state) => ({ ...state, error: null, taskCount: state.taskCount + 1, isExecutingTask: true })),
    on(AppActions.executeTaskSuccess, (state) => ({ ...state, taskCount: Math.max(state.taskCount - 1, 0), isExecutingTask: false/*(state.taskCount - 1) > 0*/ })),
    on(AppActions.setErrorTrialOver, (state) => ({ ...state, errorTrialOver: true })),
    on(AppActions.clearErrorTrialOver, (state) => ({ ...state, errorTrialOver: false })),
    on(AppActions.appError, (state, { error, url }) => ({ ...state, error, taskCount: Math.max(state.taskCount - 1, 0), isExecutingTask: excludeAppLoadingURLs.some(excludeUrl => excludeUrl.includes(url)) ? state.isExecutingTask : false })),
    on(AppActions.setLastNotification, (state, { notification }) => ({ ...state, lastNotification: notification })),
    on(AuthActions.login, (state) => ({ ...state, isExecutingTask: true })),
    on(AppActions.openSideBar, (state) => ({ ...state, openSideDrawer: true })),
    on(AuthActions.loginFailure, (state, { error }) => ({ ...state, isExecutingTask: false })),
    on(RouterActions.navigateAuth, () => ({ ...initialState })),
    on(AppActions.isShowIntercom, (state, { show }) => ({ ...state, isShowIntercom: show })),
    on(AppActions.setLastTimeActive, (state) => ({...state, errorTimeInactive: new Date().getTime() - state.lastTimeActive > state.inactivityTime ? true : false, lastTimeActive: new Date().getTime()})),
    on(AppActions.setErrorTimeInactive, (state, {errorTimeInactive}) => ({...state, errorTimeInactive})),
    on(AppActions.setInactivityTime, (state, {time}) => ({...state, inactivityTime: time})),
    on(AppActions.setRedirectedFromGoogle, (state, {redirected}) => ({...state, redirectedFromGoogle: redirected}))

)

/** Returns the isExecutingTask property from app state */
export const getIsTaskExecuting = (state: State) => state.isExecutingTask
/** Returns the error property from app state */
export const getError = (state: State) => state.error
/** Returns the lastNotification property from app state */
export const getLastNotification = (state: State) => state.lastNotification
/** Returns the isShowIntercom property from app state */
export const getIsShowIntercom = (state: State) => state.isShowIntercom
/** Returns the openSideDrawer property from app state */
export const getOpenSideDrawer = (state: State) => state.openSideDrawer
/** Returns the errorTimeInactive property from app state */
export const getErrorTimeInactive = (state: State) => state.errorTimeInactive
export const getRedirectedFromGoogle = (state: State) => state.redirectedFromGoogle
export const getErrorTrialOver = (state: State) => state.errorTrialOver;
